import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { indexBasedColorStyles } from "./data/common-data";
import { ArtifactDetailsCredit, ArtifactDetails } from "redux/slices/artifactDetails/artifactDetailsSlice";
import { setAIHelperProgressRedux, SubskillScores } from "redux/slices/aiHelperToolProgress/aiHelperProgressSlice";
import educatorService from "services/educatorService";
import useAIHelperNavigation from "../../../hooks/useAIHelperNavigation";
import useFormattedUserName from "./hooks/useFormattedUserName";
import useCurrentLearnerId from "./hooks/useCurrentLearnerId";
import HeaderNav from "components/FutureNavBasedComponents/HeaderNav";
import UnderHeaderSection from "components/FutureNavBasedComponents/PageSubheader/UnderHeaderSection";
import PillButton from "components/FutureNavBasedComponents/PillButton";
import TwoPanelView from "components/FutureNavBasedComponents/TwoPanelView";
import ChevronCollapsible from "components/FutureNavBasedComponents/ChevronCollapsible";
import SegmentedControl from "components/FutureNavBasedComponents/SegmentedControl/SegmentedControl";
import PdfViewerMUI from "components/FutureNavBasedComponents/PdfViewer/PdfViewer";
import AIBotIcon from "../../../static/icons/ai-bot.svg";
import CircleAvatar from "../../../static/icons/circle-anonymous-avatar.svg";
import { UpdateBulkSubskillAssociationsRequestBody, UpdateBulkSubskillLevelsInputArray } from "services/educatorService.types";
import { AISelectedArea, AISelectedCredit, aiScoreConfidenceValues, AIConfidenceLevel } from "redux/slices/artifactAISkillSelections/artifactAISkillSelections";
import ConfidenceIndicator from "components/FutureNavBasedComponents/ConfidenceIndicator";
import useSkillUtilities from "./hooks/useSkillUtilities";

const ViewSkillsRubric = ({ credit, skillTitle }: { credit: any; skillTitle: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [rubricData, setRubricData] = useState<any>(null);

  useEffect(() => {
    const fetchRubric = async () => {
      try {
        const response = await fetch("/NC_POG_Grades_9-12_Rubric_structured.json");
        const data = await response.json();
        const matchedSkill = data.find(
          (item: any) => item.skill.toLowerCase() === skillTitle.toLowerCase()
        );
        if (matchedSkill) {
          const matchedSubskill = matchedSkill.subskills.find(
            (sub: any) => sub.name.toLowerCase() === credit.title.toLowerCase()
          );
          setRubricData(matchedSubskill || null);
        }
      } catch (error) {
        console.error("Error fetching rubric data", error);
      }
    };
    fetchRubric();
  }, [skillTitle, credit.title]);

  return (
    <div className="mt-3">
      <button
        className="flex items-center gap-2 text-xs font-normal cursor-pointer text-[rgba(0,_125,_119,_1)]"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>View Skills Rubric</span>
        <i className={`fa-solid text-[10px] text-[rgba(0,_125,_119,_1)] ${isOpen ? "fa-chevron-up" : "fa-chevron-down"}`} />
      </button>
      {isOpen && rubricData && (
        <div className="grid grid-cols-3 gap-3 mt-3 text-xs">
          <div className="rounded-lg p-3">
            <h4 className="font-semibold mb-2">Approaching Expectations</h4>
            <p className="whitespace-pre-line">{rubricData["Approaching Expectations"]}</p>
          </div>
          <div className="rounded-lg p-3">
            <h4 className="font-semibold mb-2">Meeting Expectations</h4>
            <p className="whitespace-pre-line">{rubricData["Meeting Expectations"]}</p>
          </div>
          <div className="rounded-lg p-3">
            <h4 className="font-semibold mb-2">Exceeding Expectations</h4>
            <p className="whitespace-pre-line">{rubricData["Exceeding Expectations"]}</p>
          </div>
        </div>
      )}
      {isOpen && !rubricData && (
        <p className="text-xs text-gray-400 mt-2">No rubric data found for this subskill.</p>
      )}
    </div>
  );
};

const AreaOriginIndication = ({ isAISelected }: { isAISelected: boolean }) => {
  if (isAISelected) {
    return (
      <legend className="flex items-center mt-[6px]">
        <img src={AIBotIcon} alt="AI selected skill indicator" className="mx-1" />
        <p className="text-xs">An AI Identified Skill</p>
      </legend>
    );
  } else {
    return (
      <legend className="flex items-center mt-[6px]">
        <img src={CircleAvatar} alt="User selected skill indicator" className="mx-1" />
        <p className="text-xs">Skill added by you</p>
      </legend>
    );
  }
}

const AIHelperSubskillsReview = () => {
  const dispatch = useDispatch();
  const { handleBackToList, handleGoToSkillsReview, handleGoToShareFeedback } = useAIHelperNavigation();
  const artifactDetails = useSelector((state: any) => state.artifactDetails?.data);
  const associatedSkillsRedux = useSelector((state: any) => state.associatedSkills?.data) || {};
  // When isPlanningLibrary is true schoolModel is the school-specific child architecture.
  // Use it for areas/credits but preserve visibleLevels from the main object (set via competency model).
  const effectiveSkillsRedux = associatedSkillsRedux?.schoolModel
    ? { ...associatedSkillsRedux.schoolModel, visibleLevels: associatedSkillsRedux.visibleLevels }
    : associatedSkillsRedux;
  const artifactAISkillSelectionsRedux = useSelector((state: any) => state.artifactAISkillSelections?.data) || [];
  const aiHelperProgressRedux = useSelector((state: any) => state.aiHelperProgress?.data) || {};
  const subskillScoresRedux = aiHelperProgressRedux?.subskillReview?.subskillScores || {};

  const [previousUserCreditLevels, setPreviousUserCreditLevels] = useState<{[key: string ]: number}>({});
  const [originalAssociatedCredits, setOriginalAssociatedCredits] = useState<ArtifactDetailsCredit[]>([]);
  const { sortAreasAISelectionFirst, sortCreditsAISelectionsFirst, getAreaSubskillScoresSum } = useSkillUtilities();
  
  const { userName } = useFormattedUserName();
  const getCurrentLearnerId = useCurrentLearnerId();
  const segmentedControlOptions = Array.from({ length: (effectiveSkillsRedux.visibleLevels || 4) + 1 }, (_, i) => ({ value: i + 1, label: String(i + 1) })) 

  // Skills passed from SkillsReview (only the ones user selected)
  const userSelectedArtifactAreas: any[] = aiHelperProgressRedux?.skillReview?.finalSkillSelections || [];
  const sortedUserSelectedArtifactAreas = sortAreasAISelectionFirst(userSelectedArtifactAreas);

  useEffect(() => {
    const getAndSetInitialSubskillSelections = async () => {
      if (subskillScoresRedux) return;
      
      try {
        const evidenceResponse: ArtifactDetails = await educatorService.getEvidenceDetails(artifactDetails.id);
        if (evidenceResponse) {
          const responseCredits = evidenceResponse.areas.flatMap(area => area.credits);
          const subskillLevelsFromResponse = Object.fromEntries(
            responseCredits.map(credit => [credit.id, credit.associated_level])
          );

          const responseAreas = evidenceResponse.areas;
          const subskillLevelsFromAI: {[key: string]: number} = {};

          // check for AI selections
          userSelectedArtifactAreas.forEach((area) => {
            const aiAreaSelection = artifactAISkillSelectionsRedux.find((aiAreaSelection: AISelectedArea) => aiAreaSelection.skill_name === area.title) as AISelectedArea;
            
            area.credits.forEach((credit: ArtifactDetailsCredit) => {
              if (!!aiAreaSelection) {
                const aiCreditSelection = aiAreaSelection.subskills.find((subskill: AISelectedCredit) => subskill.subskill_name === credit.title);

                // we prioritize existing levels from the response, but if there isn't one and the AI selected the credit, we set the initial level to the AI observed level
                if (!!aiCreditSelection && !subskillLevelsFromResponse[credit.id] && aiCreditSelection.subskill_score > 0) {
                  // subskillLevelsFromAI[credit.id] = aiCreditSelection.subskill_score;
                  subskillLevelsFromAI[credit.id] = associatedSkillsRedux.visibleLevels - 1; // setting any scores above 0 to meeting expectations for now.
                }
              }
            });
          });

          dispatch(setAIHelperProgressRedux({
            ...aiHelperProgressRedux,
            subskillReview: {
              subskillScores: {                
                ...subskillLevelsFromAI,
                ...subskillLevelsFromResponse
              }
            }
          }));

          setOriginalAssociatedCredits(responseCredits);

          setPreviousUserCreditLevels({ ...subskillLevelsFromResponse });
        };
      } catch (error: any) {
        console.error("Error fetching evidence details", error);
      }
    }

    getAndSetInitialSubskillSelections();
  }, []);

  const getUpdateBulkSubskillAssociationsRequestBody = () => {
    const originalScoredSubskillIds = Object.keys(previousUserCreditLevels);
    const newAssociationsIds = Object.keys(subskillScoresRedux);
    
    const requestBody: UpdateBulkSubskillAssociationsRequestBody = {
      credit_ids: [],
      evidence_progress_ids: [],
    };

    originalScoredSubskillIds.forEach((currentOriginalScoredSubskillId) => {
      if (subskillScoresRedux[currentOriginalScoredSubskillId] === 0) {
        const associatedCredit = originalAssociatedCredits.find((credit) => `${credit.id}` === currentOriginalScoredSubskillId);
        requestBody.evidence_progress_ids.push(associatedCredit?.evidence_progress_id as number);
      }

      // it's not a newly scored subskill. Remove from newAssociationsIds
      const originallyScoredSubskillIdIndex = newAssociationsIds.findIndex((subskillId) => (subskillId === currentOriginalScoredSubskillId));
      newAssociationsIds.splice(originallyScoredSubskillIdIndex, 1);
    })

    // now newAssociationsIds only has subskills that were not originally scored.
    newAssociationsIds.forEach((subskillId) => {
      if (subskillScoresRedux[subskillId] > 0) {
        requestBody.credit_ids.push(subskillId);
      }
    });

    const finalBody: Partial<UpdateBulkSubskillAssociationsRequestBody> = {
      ...requestBody,
    };

    if (requestBody.credit_ids.length === 0) delete finalBody.credit_ids
    if (requestBody.evidence_progress_ids.length === 0) delete finalBody.evidence_progress_ids

    return finalBody;
  };

  const getUpdateLevelsRequestBody = () => {
    const learnerId = getCurrentLearnerId();
    const subskillIds = Object.keys(subskillScoresRedux);
    const requestBody: UpdateBulkSubskillLevelsInputArray = {
      updates: [],
    };

    const credits = subskillIds
      .filter((subskillId) => subskillScoresRedux[subskillId] > 0)
      .map((subskillId) => {
        const subskillScore = subskillScoresRedux[subskillId];
        return {
          "credit_id": subskillId,
          "current_level_number": subskillScore,
          "target_association_level": subskillScore,
          "is_requested": false,
        };
      });

    if (credits.length > 0) {
      requestBody.updates.push({
        "evidence_id": artifactDetails.id,
        "learner_id": Number(learnerId),
        credits,
      });
    }

    return requestBody;
  };

  const updateSubskillScoresState = (subskillScores: SubskillScores) => {
    dispatch(setAIHelperProgressRedux({
      ...aiHelperProgressRedux,
      subskillReview: {
        ...aiHelperProgressRedux.subskillReview,
        subskillScores: {
          ...aiHelperProgressRedux.subskillReview?.subskillScores,
          ...subskillScores
        }
      }
    }));
  };

  // Each subskill should have at least one scored subskill(credit) when the user clicks next
  const getAreasWithNoScoredCredits = () => {
    const areasWithNoScoredCredits: number[] = [];

    userSelectedArtifactAreas.map((area) => {
      let doesAreaHaveScoredCredits = false;
      area.credits.forEach((credit: ArtifactDetailsCredit) => {
        if (subskillScoresRedux[credit.id]) {
          doesAreaHaveScoredCredits = true;
        };
      });

      if (!doesAreaHaveScoredCredits) areasWithNoScoredCredits.push(area.id);
    });

    return areasWithNoScoredCredits;
  };

  const setScoredSelectedAreas = (unscoredAreaIds: number[]) => {
    let updatedArtifactAreas = [ ...aiHelperProgressRedux.skillReview.selectedAreas ];

    unscoredAreaIds.forEach((id) => {
      updatedArtifactAreas = updatedArtifactAreas.filter((area) => area.id !== id);
    });

    dispatch(
      setAIHelperProgressRedux({
        ...aiHelperProgressRedux,
        subskillReview: {
          ...aiHelperProgressRedux.subskillReview,
          selectedAreasWithScoredCredits: updatedArtifactAreas,
        }
      })
    );
  };

  return (
    <div className="tailwind-page font-beausite text-typography-charcoal pb-5">
      <div className="max-w-full mx-[13px]">
        <HeaderNav />
      </div>

      <div className="flex justify-between">
        <UnderHeaderSection
          backButtonLabel="Learner Artifacts List"
          onBackButtonClick={() => {handleBackToList()}}
          pageMainHeader="Review & Rate Subskills"
        />

        {/* <div className="flex items-center pr-[31px]">
          <PillButton onClick={() => {}} label="Get Started" variant="dark" />
        </div> */}
      </div>
      <TwoPanelView
        leftPanelSubheader={(<p className="text-sm">For the primary Skills that we observed, we have preselected ratings for their Subskills. Please make adjustments to align with your observations.</p>)}  
        // "For the primary skills that we observed, we have preselected ratings for their subskills. Please make adjustments to align with your observations"
        LeftPanelContent={
          (
            <div className="mt-3 h-min">
              {sortedUserSelectedArtifactAreas.map((skill: any, skillIndex: number) => {
                const credits = sortCreditsAISelectionsFirst(skill.title, skill.credits || []);
                const aiAreaSelection = artifactAISkillSelectionsRedux?.find((aiAreaSelection: AISelectedArea) => aiAreaSelection.skill_name === skill.title) as AISelectedArea | undefined;
                const isSkillScoredByAI = getAreaSubskillScoresSum(aiAreaSelection) > 0;

                let numberOfUserSelectedSkillsRendered = 0;
                if (!aiAreaSelection) numberOfUserSelectedSkillsRendered++;
                const shouldAddDivider = numberOfUserSelectedSkillsRendered === 1 && !!artifactAISkillSelectionsRedux?.length;

                return (
                  <>
                    {
                      /* adding a divider between AI-selected and user-selected skills */
                      shouldAddDivider && (
                        <>
                          <div className="h-[2px] w-full px-6 bg-outline-charcoal mt-8 mb-6" />
                          <p className="mb-[14px] text-sm">For primary Skills that were added by you, please provide ratings for each Subskill you observed.</p>
                        </>
                      )
                    }
                    <div key={skill.id} className={`rounded-lg border px-6 pt-[18px] pb-9 mb-3 ${indexBasedColorStyles[skillIndex % indexBasedColorStyles.length] || ""}`}>
                      <div className="flex justify-between items-center w-full mb-14">
                        <h3 className="font-semibold">{skill.title}</h3>

                        <AreaOriginIndication isAISelected={!!aiAreaSelection && isSkillScoredByAI} />
                      </div>

                      {credits.map((credit: any, creditIndex: number) => {
                        const key = `${skill.id}-${credit.id}`;
                        const aiCreditSelection = aiAreaSelection ? aiAreaSelection?.subskills.find((subskill: AISelectedCredit) => subskill.subskill_name === credit.title) : null;

                        return (
                          <ChevronCollapsible
                            key={key}
                            HeaderContent={(
                              <div className="flex items-center px-1 w-[95%]">
                                <div className="flex items-center w-full">
                                  <SegmentedControl
                                    onChange={({ selectedOptionIndex }: { selectedOptionIndex: number }) => {
                                      // setSubskillSelections((prev) => ({
                                      //   ...prev,
                                      //   [credit.id]: selectedOptionIndex,
                                      // }));

                                      // dispatch(setSubskillScoresRedux({
                                      //   ...subskillScoresRedux,
                                      //   [credit.id]: selectedOptionIndex,
                                      // }));

                                      updateSubskillScoresState({
                                        ...subskillScoresRedux,
                                        [credit.id]: selectedOptionIndex,
                                      });
                                    }}
                                    options={segmentedControlOptions}
                                    thresholdIndicators={
                                      [{
                                        indicatorLabel: creditIndex === 0 ? "Meeting Expectations" : "",
                                        indicatorIndex: associatedSkillsRedux.visibleLevels - 1,
                                      }]
                                    }
                                    className="w-[266px] min-w-[266px] flex-shrink-0 mr-4"
                                    selectedOptionIndex={
                                      subskillScoresRedux[credit.id] !== undefined
                                        ? subskillScoresRedux[credit.id]
                                        : 0
                                    }
                                  />
                                  <p className="text-sm truncate" title={credit.title}>{credit.title}</p>
                                  {
                                    aiCreditSelection && aiCreditSelection.subskill_score > 0
                                      && (<img src={AIBotIcon} alt="AI selected skill indicator" className="ml-1" />)
                                  }
                                </div>
                              </div>
                            )}
                            BodyContent={(
                              <div className="w-full pb-7 px-3">
                                <h5 className="text-sm mb-1">Details</h5>
                                <ul className="list-disc text-xs ml-6">
                                  {(aiCreditSelection && aiCreditSelection.subskill_score > 0) && (
                                    <>
                                      <li className="mb-4">
                                        <p><b>Reason: </b> {aiCreditSelection.subskill_reason}</p>
                                      </li>
                                      <li className="mb-4">
                                        <p><b>Confidence: </b> The AI observed <b>{aiCreditSelection.subskill_name}</b> with the following level of confidence:</p>
                                        <ConfidenceIndicator
                                          confidenceLevel={aiScoreConfidenceValues[aiCreditSelection.subskill_confidence as AIConfidenceLevel]}
                                          className="mt-1"
                                        />
                                      </li>
                                    </>
                                  )}
                                  <li>
                                    <p><b>Definition: </b> {credit.title}</p>
                                    {credit.description && (
                                      <p className="ml-2 mt-1">{credit.description}</p>
                                    )}
                                  </li>
                                </ul>
                                <ViewSkillsRubric credit={credit} skillTitle={skill.title} />
                              </div>
                            )}
                            className="mb-[10px] bg-white overflow-y-visible"
                          />
                        )
                      })}

                      <div className="flex items-start px-8 mt-1">
                        <div
                          className="mr-2 flex justify-between items-start flex-shrink-0"
                          style={{ width: "100%", maxWidth: 266 }}
                        >
                          <span className="text-[10px] text-[#455459] text-left leading-tight whitespace-pre-line">
                            Not
                            <br />
                            Demonstrated
                          </span>
                          <span className="text-[10px] text-[#455459] text-right leading-tight whitespace-pre-line">
                            Exceeding
                            <br />
                            Expectations
                          </span>
                        </div>
                      </div>
                    </div>
                  </>

                );
              })}

              <div className="flex items-center mt-6">
                <PillButton variant="light" label="Back" onClick={() => {handleGoToSkillsReview()}} outerDivClassName="mr-[14px]"/>
                <PillButton
                  variant="dark"
                  label="Next"
                  onClick={async () => {
                    const learnerId = getCurrentLearnerId();
                    await educatorService.updateBulkSubskillAssociations({
                      artifactId: artifactDetails.id,
                      learnerId,
                      requestBody: getUpdateBulkSubskillAssociationsRequestBody(),
                    });

                    await educatorService.updateBulkSubskillLevels(getUpdateLevelsRequestBody())

                    const unscoredAreas = getAreasWithNoScoredCredits();
                    if (unscoredAreas) setScoredSelectedAreas(unscoredAreas);

                    handleGoToShareFeedback();
                  }}
                />
              </div>
            </div>
          )
        }
        leftPanelVariant="light"
        rightPanelHeader={artifactDetails?.title}
        rightPanelSubheader={`Learner: ${userName}`}
        rightPanelVariant="dark"
        RightPanelContent={(<PdfViewerMUI file={artifactDetails?.fileUrl} />)}
      />
    </div>
  );
};

export default AIHelperSubskillsReview;

